import type { Geofence as SitumGeofence } from "@situm/sdk-js";
import { t } from "i18next";

import { StarIcon } from "@/features/app/components/icons";
import { AccessibleColorThemes, ColorThemes } from "@/features/app/constants";
import type { GenericOverlayFeature } from "@/features/editor/types";
import { readOverlayFeaturesFromCustomField } from "@/features/editor/utils/overlayFeaturesUtils";
import { CoordinateConverterFlatEarth } from "@/libs/geo";
import { autofillGeoserverQueryParams } from "@/libs/geoserver";
import type IMDFFeature from "@/libs/imdf/feature";
import IMDF from "@/libs/imdf/imdf";
import IMDFTheme from "@/libs/imdf/theme";
import { Geofence, Geofences } from "@/libs/models/geofence";
import { Paths } from "@/libs/models/path";
import { getFontNameFromURL, loadFont } from "./fonts";
import {
  Building,
  BuildingGeometry,
  Corners,
  type CustomField,
  CustomFields,
  FAV_POI_CATEGORY_ID,
  Floor,
  LatLng,
  License,
  Organization,
  Poi,
  PoiCategories,
  PoiCategory,
  Pois,
  Position,
} from "./models";

// Domain Factories / Parsers

function buildCategoryTree(categories: any[]): PoiCategory[] {
  const categoryMap = new Map<number, PoiCategory>();
  const roots: PoiCategory[] = [];

  // Build the categoryMap
  for (const category of categories) {
    categoryMap.set(
      category.id,
      new PoiCategory(
        category.id,
        category.code,
        category.nameEn,
        cfMapper(category.customFields),
        category.iconUrl,
        category.selectedIconUrl,
        undefined,
        category.parentPoiCategoryId,
      ),
    );
  }

  // Build the category tree
  for (const category of categories) {
    const node = categoryMap.get(category.id) as PoiCategory;
    if (category.parentPoiCategoryId == null) {
      roots.push(node);
    }
    const parent = categoryMap.get(category.parentPoiCategoryId);
    if (parent) {
      if (!parent.subCategories) {
        parent.subCategories = [];
      }
      parent.subCategories.push(node);
    }
  }

  return roots;
}

export function poiCategoryFactory(data: any): PoiCategories {
  return new PoiCategories(buildCategoryTree(data)).sortByName();
}

function cfMapper(cfs: any[]): CustomFields<string> {
  const cfsArray: CustomField[] = [];
  cfs?.forEach((e) => {
    const cf = { key: e.key, value: e.value };
    cfsArray.push(cf);
  });
  return new CustomFields(cfsArray);
}

export function poiMapper(
  pois: any[],
  coordinateConverter: CoordinateConverterFlatEarth,
) {
  const poiList = pois.map((e) => {
    // Force poi cartesian coordinate re-calculation (API conversion is flawed)
    const cartesian = coordinateConverter.toCartesian({
      lat: e.location.lat,
      lng: e.location.lng,
    });
    return new Poi(
      e.id,
      e.name,
      new Position(
        e.buildingId,
        e.floorId,
        e.location.lat,
        e.location.lng,
        cartesian.x,
        cartesian.y,
      ),
      e.categoryId,
      e.categoryIds,
      e.info,
      e.buildingId,
      cfMapper(e.customFields),
      e?.icon,
      e?.selectedIcon,
    );
  });

  return new Pois(poiList);
}

function readCustomFloorplan(
  floor: Floor,
  geometry: BuildingGeometry,
  corners: Corners,
) {
  let url = floor.getCustomFieldValue("geoserver_map_url");
  if (!url) return null;

  url = decodeURIComponent(url);

  const geoserverData = autofillGeoserverQueryParams(
    url,
    geometry.width,
    geometry.length,
    corners,
  );

  return {
    bounds: geoserverData.bbox,
    url: geoserverData.url,
  };
}

function floorMapper(
  floorsData: {
    id: number;
    level: number;
    maps: any;
    name: string;
    customFields: any[];
  }[],
  geometry: BuildingGeometry,
  corners: Corners,
) {
  const buildingBounds: [number, number][] = [
    [corners.se.lng, corners.se.lat],
    [corners.ne.lng, corners.ne.lat],
    [corners.nw.lng, corners.nw.lat],
    [corners.sw.lng, corners.sw.lat],
  ];

  return (
    floorsData
      .map((e) => {
        try {
          const floor = new Floor(
            e.id,
            e.level,
            { bounds: buildingBounds, url: e.maps.mapUrl },
            e.name,
            cfMapper(e.customFields),
          );

          // Read custom floorplans from customFields
          const customFloorplan = readCustomFloorplan(floor, geometry, corners);
          if (customFloorplan != null) {
            floor.floorplan = customFloorplan;
          }
          return floor;
        } catch (error) {
          console.error("Error parsing floor", { data: e, error });
          return null;
        }
      })
      // Filter out null values
      .filter((e) => e != null) as Floor[]
  );
}

function cornerFactoryFromCornersAndCenter(
  corners: { lat: number; lng: number }[],
  _center: any,
) {
  const nw = corners[1];
  const ne = corners[0];
  const se = corners[3];
  const sw = corners[2];

  return new Corners(
    new LatLng(nw.lat, nw.lng),
    new LatLng(ne.lat, ne.lng),
    new LatLng(se.lat, se.lng),
    new LatLng(sw.lat, sw.lng),
  );
}

function pathsMapper(
  pathsData: any,
  coordinateConverter: CoordinateConverterFlatEarth,
): Paths | null {
  if (!pathsData) return null;
  const nodes = pathsData.nodes.map((n: any) => {
    const coords = coordinateConverter.toCoordinate({
      x: n.x,
      y: n.y,
    });

    n.lat = coords.lat;
    n.lng = coords.lng;

    return n;
  });
  return new Paths({
    links: pathsData.links,
    nodes: nodes,
  });
}

async function fetchIMDF(
  buildingCFs: CustomFields<string>,
  floors: Floor[],
): Promise<IMDF> {
  const geojsonUrl = buildingCFs.getValueOfKey("geojson_map_url");

  let geojson: any = null;
  const geojsonsByFloorLevel: Record<number, IMDFFeature[]> = {};

  const geojsonByFloorLevelURLs = floors.reduce<Record<number, string>>(
    (acc, floor) => {
      const geojsonField = floor.customFields.cf.find(
        (customField) => customField.key === "geojson_map_url",
      );

      if (geojsonField) {
        acc[floor.level] = geojsonField.value;
      }

      return acc;
    },
    {},
  );

  if (geojsonUrl) {
    geojson = await fetch(geojsonUrl)
      .then((r) => r.json())
      .catch((e) => {
        console.error(`Unable to parse`, geojsonUrl, e);
      });
  } else if (
    geojsonByFloorLevelURLs &&
    Object.keys(geojsonByFloorLevelURLs).length > 0
  ) {
    // Initialize an empty object to store fetched geojsons indexed by floor level
    for (const level in geojsonByFloorLevelURLs) {
      const url = geojsonByFloorLevelURLs[level];

      try {
        geojsonsByFloorLevel[level] = (await fetch(url).then((r) =>
          r.json(),
        )) as IMDFFeature[];
      } catch (e) {
        console.error(`Unable to parse geojson from`, url, e);
      }
    }
    // Now, geojsonsByFloorLevel contains all the fetched geojsons indexed by floor level.
  }

  const imdf = new IMDF(
    {},
    geojsonUrl || Object.keys(geojsonByFloorLevelURLs).join("-"),
  );
  if (geojson) {
    imdf.createFromBuildingGeoJSON(geojson);
  } else if (Object.keys(geojsonsByFloorLevel).length !== 0) {
    imdf.createFromFloorsGeoJSON(geojsonsByFloorLevel);
  }

  return imdf;
}

async function fetchIMDFTheme(
  buildingCFs: CustomFields<string>,
  customFieldName: string,
  domain: string,
): Promise<IMDFTheme | null> {
  if (!domain) return;

  let geojsonTheme: any = null;
  let geojsonThemeURL = buildingCFs.getValueOfKey(customFieldName);

  if (geojsonThemeURL) {
    if (!geojsonThemeURL.includes("://")) {
      geojsonThemeURL = `https://${domain}${geojsonThemeURL}`;
    }

    geojsonTheme = await fetch(geojsonThemeURL)
      .then((r) => r.json())
      .catch((e) => {
        console.error(`Unable to parse`, geojsonThemeURL, e);
      });
  }
  if (geojsonTheme) return new IMDFTheme(geojsonTheme);
  else return null;
}

export const imdfMapper = (
  buildingCFs: CustomFields<string>,
  floors: Floor[],
  domain: string,
) => {
  const imdfPromise = fetchIMDF(buildingCFs, floors);
  const imdfThemePromise = fetchIMDFTheme(
    buildingCFs,
    "geojson_theme_url",
    domain,
  );
  const imdfHighContrastDarkThemePromise = fetchIMDFTheme(
    buildingCFs,
    "geojson_theme_url_high_contrast_dark",
    domain,
  );

  return imdfPromise.then(async (imdf) => {
    const imdfTheme = await imdfThemePromise;
    const imdfHighContrastDarkTheme = await imdfHighContrastDarkThemePromise;

    if (imdf) {
      if (imdfTheme) {
        imdf.loadTheme(ColorThemes.light, imdfTheme);
      } else {
        // Load default theme for light mode
        imdf.loadTheme(ColorThemes.light, new IMDFTheme());
      }
      if (imdfHighContrastDarkTheme) {
        imdf.loadTheme(
          AccessibleColorThemes.highContrastDark,
          imdfHighContrastDarkTheme,
        );
      }
      imdf.changeTheme(ColorThemes.light);
    }

    return imdf;
  });
};

// TODO please do not use "any" for types :'(
export function buildingMapper(buildingData: any) {
  if (buildingData.corners.length === 0 || buildingData.floors.length === 0)
    return null;

  // Building basic info
  const corners = cornerFactoryFromCornersAndCenter(
    buildingData.corners,
    buildingData.location,
  );

  const geometry = new BuildingGeometry(
    buildingData.location,
    corners,
    buildingData.dimensions.width,
    buildingData.dimensions.length,
    buildingData.rotation,
  );

  // Tmp coordinate converter to convert data before having the Building model created
  const coordinateConverter = new CoordinateConverterFlatEarth(
    geometry.corners.bottomRight,
    geometry.corners.bottomLeft,
    geometry.corners.topLeft,
    geometry.corners.topRight,
    geometry.width,
    geometry.length,
  );

  const cfs = cfMapper(buildingData.customFields);
  const floors = floorMapper(buildingData.floors, geometry, corners);

  // Paths data
  const paths = buildingData.paths
    ? pathsMapper(buildingData.paths, coordinateConverter)
    : undefined;

  const pathTags = [
    ...new Set(
      paths?.links
        ?.flatMap((link: any) => link?.tags || [])
        .sort()
        .filter((tag: string) => !tag.startsWith("#")) || [],
    ),
  ] as string[];

  pathTags.push("notag");

  // Geofences
  const geofences = buildingData.geofences
    ? new Geofences(
        buildingData.geofences.map((g: SitumGeofence) => {
          return new Geofence(g);
        }),
      )
    : undefined;

  // Pois
  const pois = buildingData.pois
    ? poiMapper(buildingData.pois, coordinateConverter)
    : undefined;

  // Read overlay features from custom field
  const overlaysCustomFieldData: string | null =
    buildingData.customFields.find((val: any) => val.key === "3d_models")
      ?.value || null;

  let overlayFeatures: Record<string, GenericOverlayFeature> | undefined;
  try {
    if (overlaysCustomFieldData != null) {
      overlayFeatures = readOverlayFeaturesFromCustomField(
        overlaysCustomFieldData,
      );
    }
  } catch (e) {
    console.warn(
      `Could not load overlay features custom field for building`,
      { building: buildingData?.name ?? buildingData?.id },
      "Check that the custom field string is properly formed and try loading the viewer again.",
      e,
    );
  }
  if (overlayFeatures) {
    Object.keys(overlayFeatures).forEach((key) => {
      const feature = overlayFeatures[key];
      if (!feature?.properties?.fontFamily) return;
      loadFont(
        getFontNameFromURL(feature?.properties?.fontFamily),
        feature?.properties?.fontFamily,
      );
    });
  }

  return new Building(
    buildingData.id,
    buildingData.name,
    buildingData.address,
    buildingData.info,
    buildingData.pictureUrl,
    geometry,
    cfs,
    floors,
    pois,
    paths,
    geofences,
    null,
    pathTags,
    overlayFeatures,
  );
}

const licenseMapper = (data: any) => {
  return new License(data.id, data.licenseType, new Date(data.expirationDate));
};

export const organizationMapper = (data: any) => {
  return new Organization(
    data.id,
    data.name,
    licenseMapper(data.license),
    cfMapper(data.customFields),
    data?.logoLoginPath,
    data?.logoPath,
    data?.colors,
  );
};

export const createFavoritePoiCategory = async () => {
  return new PoiCategory(
    FAV_POI_CATEGORY_ID,
    "favs",
    t("favorites.category_name"),
    new CustomFields([]),
    "",
    "",
    undefined,
    undefined,
    StarIcon,
  );
};
